// A reason asked for under the key that asked. Dismissing a suggestion is a
// cancel with a reason; the same few words also put a run on hold or cancel
// one, so the labels are props and the default is the dismiss.

import React, { useEffect, useRef, useState } from 'react';
import Popover from './Popover';
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import FadeIn from '../components/ui/FadeIn';
import { TextArea } from '../components/ui/Field';

export interface DismissDialogProps {
  triggerLabel?: string;
  triggerClassName: string;
  tooltip?: string;
  triggerAriaLabel?: string;
  title?: string;
  fieldLabel?: string;
  confirmLabel?: string;
  /** A required reason refuses an empty field and says why. */
  required?: boolean;
  tone?: 'primary' | 'danger';
  align?: 'left' | 'right';
  disabled?: boolean;
  /** Resolve true to close; false keeps the field open (the page toasted why). */
  onConfirm: (reason: string) => Promise<boolean>;
}

const CONFIRM_TONE = {
  primary: buttonClass('filled', 'md'),
  danger: buttonClass('danger', 'md'),
};

const DismissDialog: React.FC<DismissDialogProps> = ({
  triggerLabel = 'Dismiss',
  triggerClassName,
  tooltip,
  triggerAriaLabel,
  title = 'Dismiss this suggestion',
  fieldLabel = 'Why is it not needed?',
  confirmLabel = 'Dismiss',
  required = true,
  tone = 'danger',
  align,
  disabled,
  onConfirm,
}) => (
  <Popover
    triggerLabel={triggerLabel}
    triggerClassName={triggerClassName}
    tooltip={tooltip}
    triggerAriaLabel={triggerAriaLabel}
    title={title}
    disabled={disabled}
    align={align}
  >
    {(close) => (
      <ReasonForm
        fieldLabel={fieldLabel}
        confirmLabel={confirmLabel}
        required={required}
        tone={tone}
        onCancel={close}
        onConfirm={async (reason) => {
          if (await onConfirm(reason)) close();
        }}
      />
    )}
  </Popover>
);

const ReasonForm: React.FC<{
  fieldLabel: string;
  confirmLabel: string;
  required: boolean;
  tone: 'primary' | 'danger';
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}> = ({ fieldLabel, confirmLabel, required, tone, onCancel, onConfirm }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  // A successful confirm closes the popover before the await settles, so the
  // busy flag is only cleared while the form is still mounted.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const submit = async () => {
    const reason = text.trim();
    if (required && !reason) {
      setError('Add a few words so the next person knows why.');
      fieldRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await onConfirm(reason);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">{fieldLabel}</span>
        <TextArea
          ref={fieldRef}
          rows={3}
          value={text}
          maxLength={1000}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          error={!!error}
        />
      </label>
      <FadeIn key={error ? 'error' : 'hint'}>
        {error ? (
          <Alert tone="error" variant="text">{error}</Alert>
        ) : (
          <p className="text-xs text-on-surface-variant">Enter confirms. Shift and Enter starts a new line.</p>
        )}
      </FadeIn>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className={buttonClass('quiet', 'md')}>
          Never mind
        </button>
        <button
          type="submit"
          disabled={busy}
          className={CONFIRM_TONE[tone]}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </form>
  );
};

export default DismissDialog;
